import React, { useState, useEffect } from 'react';
import { getBookings, updateBooking, deleteBooking, getBlockedDates } from '../services/pocketbase';
import { usePocketBaseRealtime } from '../hooks/usePocketBase';
import { FiSearch, FiCalendar, FiList, FiUser, FiClock, FiMessageSquare, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { format, addMinutes, parse, startOfWeek, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AdminBookings.css';

const locales = {
    'fr': fr,
}

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
})

function AdminBookings() {
    const [bookings, setBookings] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all'); // all, pending, confirmed, cancelled, completed
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

    useEffect(() => {
        loadData();
    }, []);

    // Écouter les mises à jour en temps réel
    usePocketBaseRealtime('active', (e) => {
        if (e.action === 'create') {
            setBookings(prev => [e.record, ...prev]);
        } else if (e.action === 'update') {
            setBookings(prev => prev.map(b => b.id === e.record.id ? e.record : b));
        } else if (e.action === 'delete') {
            setBookings(prev => prev.filter(b => b.id !== e.record.id));
        }
    });

    const loadData = async () => {
        setLoading(true);
        try {
            // Charger les réservations
            const bookingsResult = await getBookings({
                sort: '-created',
                expand: 'service,user'
            });
            setBookings(bookingsResult.items || []);

            // Charger les dates bloquées
            try {
                const blockedResult = await getBlockedDates();
                setBlockedDates(blockedResult);
            } catch (err) {
                console.error("Erreur chargement dates bloquées (possible collection manquante)", err);
                // On continue même si ça échoue
            }

        } catch (error) {
            console.error('Erreur de chargement global:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updateBooking(id, { status: newStatus });
            // Optimistic update handled by realtime or reload, but let's update local state for snappiness
            setBookings(prev => prev.map(booking =>
                booking.id === id ? { ...booking, status: newStatus } : booking
            ));
        } catch (error) {
            console.error('Erreur de mise à jour:', error);
            alert('Erreur lors de la mise à jour du statut');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) {
            return;
        }

        try {
            await deleteBooking(id);
            setBookings(prev => prev.filter(booking => booking.id !== id));
        } catch (error) {
            console.error('Erreur de suppression:', error);
            alert('Erreur lors de la suppression');
        }
    };

    // Filter logic
    const filteredBookings = bookings.filter(booking => {
        const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
            (booking.client_name || booking.name || '').toLowerCase().includes(searchLower) ||
            (booking.client_email || booking.email || '').toLowerCase().includes(searchLower);
        return matchesStatus && matchesSearch;
    });

    // Calendar Events Construction
    const bookingEvents = bookings.map(b => {
        const startDate = new Date(b.date);
        // Default duration 60 mins if service duration missing
        const duration = b.expand?.service?.duration || 60;
        return {
            id: b.id,
            title: `${b.client_name || b.name} - ${b.expand?.service?.title || 'Service'}`,
            start: startDate,
            end: addMinutes(startDate, duration),
            resource: b,
            allDay: false,
            type: 'booking'
        };
    });

    const blockedEvents = blockedDates.map(b => {
        const date = new Date(b.date);
        return {
            id: b.id,
            title: `⛔ ${b.reason || 'Fermé'}`,
            start: date,
            end: date, // React Big Calendar handles single day allDay events fine with same start/end
            resource: { status: 'blocked' },
            allDay: true,
            type: 'blocked'
        };
    });

    const events = [...bookingEvents, ...blockedEvents];

    // Stats
    const stats = {
        total: bookings.length,
        pending: bookings.filter(b => b.status === 'pending').length,
        confirmed: bookings.filter(b => b.status === 'confirmed').length,
        completed: bookings.filter(b => b.status === 'completed').length,
        cancelled: bookings.filter(b => b.status === 'cancelled').length
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: '#ffc107',
            confirmed: '#28a745',
            completed: '#6c757d',
            cancelled: '#dc3545',
            blocked: '#e74c3c' // Rouge pour les jours bloqués
        };
        return colors[status] || '#6c757d';
    };

    const eventStyleGetter = (event) => {
        const backgroundColor = getStatusColor(event.resource.status);

        let style = {
            backgroundColor,
            borderRadius: '4px',
            opacity: 0.9,
            color: 'white',
            border: 'none',
            display: 'block',
            fontSize: '0.85rem'
        };

        if (event.type === 'blocked') {
            style.opacity = 1;
            style.fontWeight = 'bold';
        }

        return { style };
    };

    if (loading) return (
        <div className="admin-bookings-loading">
            <div className="spinner-large"></div>
            <p>Chargement des réservations...</p>
        </div>
    );

    return (
        <div className="admin-bookings">
            <div className="admin-bookings-header">
                <div className="header-text">
                    <h1>📅 Gestion des Réservations</h1>
                    <p>Suivez et gérez l'ensemble des rendez-vous du salon.</p>
                </div>

                {/* View Toggle */}
                <div className="view-toggle-container">
                    <button
                        className={`btn-view ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                    >
                        <FiList /> Liste
                    </button>
                    <button
                        className={`btn-view ${viewMode === 'calendar' ? 'active' : ''}`}
                        onClick={() => setViewMode('calendar')}
                    >
                        <FiCalendar /> Calendrier
                    </button>
                </div>
            </div>

            {viewMode === 'list' ? (
                <>
                    {/* Stats Cards */}
                    <div className="stats-grid">
                        <div className="stat-card stat-total">
                            <div className="stat-icon">📊</div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total</div>
                            </div>
                        </div>
                        <div className="stat-card stat-pending">
                            <div className="stat-icon">⏳</div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.pending}</div>
                                <div className="stat-label">En attente</div>
                            </div>
                        </div>
                        <div className="stat-card stat-confirmed">
                            <div className="stat-icon">✅</div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.confirmed}</div>
                                <div className="stat-label">Confirmés</div>
                            </div>
                        </div>
                        <div className="stat-card stat-completed">
                            <div className="stat-icon">🎉</div>
                            <div className="stat-info">
                                <div className="stat-value">{stats.completed}</div>
                                <div className="stat-label">Terminés</div>
                            </div>
                        </div>
                    </div>

                    {/* Filters & Search */}
                    <div className="bookings-controls">
                        <div className="filter-buttons">
                            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                                <button
                                    key={status}
                                    className={filterStatus === status ? 'active' : ''}
                                    onClick={() => setFilterStatus(status)}
                                >
                                    {status === 'all' ? 'Tous' :
                                        status === 'pending' ? 'En attente' :
                                            status === 'confirmed' ? 'Confirmés' :
                                                status === 'completed' ? 'Terminés' : 'Annulés'}
                                </button>
                            ))}
                        </div>

                        <div className="search-box">
                            <FiSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher (nom, email...)"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* List View */}
                    <div className="bookings-list">
                        {filteredBookings.length === 0 ? (
                            <div className="no-bookings">
                                <div className="no-bookings-icon">📭</div>
                                <p>Aucune réservation trouvée.</p>
                            </div>
                        ) : (
                            filteredBookings.map(booking => (
                                <div key={booking.id} className="booking-card">
                                    <div className="booking-card-header">
                                        <div className="booking-customer">
                                            <h3>{booking.client_name || booking.name || 'Client Inconnu'}</h3>
                                            <div className="booking-contact">
                                                <span>{booking.client_email || booking.email}</span>
                                                <span>{booking.client_phone || booking.phone}</span>
                                            </div>
                                        </div>
                                        <div className="booking-status" style={{ backgroundColor: getStatusColor(booking.status) }}>
                                            {booking.status === 'pending' && 'En attente'}
                                            {booking.status === 'confirmed' && 'Confirmé'}
                                            {booking.status === 'cancelled' && 'Annulé'}
                                            {booking.status === 'completed' && 'Terminé'}
                                        </div>
                                    </div>

                                    <div className="booking-card-body">
                                        <div className="booking-info">
                                            <div className="info-item">
                                                <FiUser className="info-icon" />
                                                <div className="info-text">
                                                    <strong>Service : </strong>
                                                    {booking.expand?.service?.title || 'Service inconnu'}
                                                    ({booking.expand?.service?.duration} min)
                                                </div>
                                            </div>
                                            <div className="info-item">
                                                <FiCalendar className="info-icon" />
                                                <div className="info-text">
                                                    <strong>Date : </strong>
                                                    {format(new Date(booking.date), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                                                </div>
                                            </div>
                                            {booking.notes && (
                                                <div className="info-item">
                                                    <FiMessageSquare className="info-icon" />
                                                    <div className="info-text">
                                                        <strong>Note : </strong> {booking.notes}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="booking-card-footer">
                                        <div className="status-actions-group">
                                            {booking.status === 'pending' && (
                                                <div className="quick-actions">
                                                    <button
                                                        className="btn-quick-confirm"
                                                        onClick={() => handleStatusChange(booking.id, 'confirmed')}
                                                        title="Confirmer le RDV"
                                                    >
                                                        <FiCheck /> Confirmer
                                                    </button>
                                                    <button
                                                        className="btn-quick-cancel"
                                                        onClick={() => handleStatusChange(booking.id, 'cancelled')}
                                                        title="Annuler le RDV"
                                                    >
                                                        <FiX /> Annuler
                                                    </button>
                                                </div>
                                            )}

                                            <div className="status-select-wrapper">
                                                <label>Statut :</label>
                                                <select
                                                    value={booking.status}
                                                    onChange={(e) => handleStatusChange(booking.id, e.target.value)}
                                                    className="status-select"
                                                >
                                                    <option value="pending">En attente</option>
                                                    <option value="confirmed">Confirmé</option>
                                                    <option value="completed">Terminé</option>
                                                    <option value="cancelled">Annulé</option>
                                                </select>
                                            </div>
                                        </div>
                                        <button className="delete-btn" onClick={() => handleDelete(booking.id)}>
                                            <FiTrash2 /> Supprimer
                                        </button>
                                    </div>
                                    <div className="booking-meta">
                                        Créé le {format(new Date(booking.created), "d/MM/yyyy HH:mm")}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            ) : (
                /* Calendar View */
                <div className="calendar-wrapper">
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: 750 }}
                        messages={{
                            next: "Suivant",
                            previous: "Précédent",
                            today: "Aujourd'hui",
                            month: "Mois",
                            week: "Semaine",
                            day: "Jour",
                            agenda: "Agenda",
                            date: "Date",
                            time: "Heure",
                            event: "Événement",
                            noEventsInRange: "Aucun événement dans cette période."
                        }}
                        defaultView="week"
                        eventPropGetter={eventStyleGetter}
                        culture='fr'
                    />
                </div>
            )}
        </div>
    );
}

export default AdminBookings;
